import * as THREE from 'three/webgpu';
import * as TSL from 'three/tsl';

export class Grass extends THREE.Mesh {
    constructor({
            chunkSize = 20, // size of chunk in meters
            details = 100,
            centerPosRandomness = 0.5,
            bladeWidth = 0.3,
            bladeHeight = 0.5,
            bladeHeightRandomness = 0.5,
            bladeColor = new THREE.Color(0.6, 0.8, 0),
        } = {}) {
        super();

        const textureLoader = new THREE.TextureLoader();
        const noiseTexture = textureLoader.load(
            '/tex/Perlin 4 - 256x256.png',
            () => {
                console.log('onLoaded');
            });
        noiseTexture.wrapS = noiseTexture.wrapT = THREE.RepeatWrapping;

        this.frustumCulled = false;

        const bladeCount = details * details; // number of blades
        const fragmentSize = chunkSize / details;

        const bladeCenters = new Float32Array(bladeCount * 2 * 3);
        const bladePositions = new Float32Array(bladeCount * 3 * 3);
        const bladeNormals = new Float32Array(bladeCount * 3 * 3);
        const indices = [];

        for (let iX = 0; iX < details; iX++) {
            for (let iZ = 0; iZ < details; iZ++) {
                // center attribute
                const iCenter = (iX * details + iZ) * 2 * 3;
                const centerX = (iX / details - 0.5) * chunkSize + fragmentSize * 0.5
                    + (Math.random() - 0.5) * fragmentSize * centerPosRandomness; // add randomness
                const centerZ = (iZ / details - 0.5) * chunkSize + fragmentSize * 0.5
                    + (Math.random() - 0.5) * fragmentSize * centerPosRandomness;
                bladeCenters[iCenter + 0] = centerX;
                bladeCenters[iCenter + 1] = centerZ;
                bladeCenters[iCenter + 2] = centerX;
                bladeCenters[iCenter + 3] = centerZ;
                bladeCenters[iCenter + 4] = centerX;
                bladeCenters[iCenter + 5] = centerZ;

                // position attribute
                const iPosition = (iX * details + iZ) * 3 * 3;
                bladePositions[iPosition + 0] = 0;
                bladePositions[iPosition + 1] = bladeHeight * (1 - Math.random() * bladeHeightRandomness);;
                bladePositions[iPosition + 2] = 0;
                bladePositions[iPosition + 3] = -bladeWidth / 2;
                bladePositions[iPosition + 4] = 0;
                bladePositions[iPosition + 5] = 0;
                bladePositions[iPosition + 6] = bladeWidth / 2;
                bladePositions[iPosition + 7] = 0;
                bladePositions[iPosition + 8] = 0;

                // normal attribute
                const iNormal = (iX * details + iZ) * 3 * 3;
                const BRnormal = new THREE.Vector3(0, 0, 1);
                // BRnormal.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI/6);
                bladeNormals[iNormal + 0] = BRnormal.x;
                bladeNormals[iNormal + 1] = BRnormal.y;
                bladeNormals[iNormal + 2] = BRnormal.z;
                const BLnormal = new THREE.Vector3(0, 0, 1);
                // BLnormal.applyAxisAngle(new THREE.Vector3(0, 1, 0), -Math.PI/6);
                bladeNormals[iNormal + 3] = BLnormal.x;
                bladeNormals[iNormal + 4] = BLnormal.y;
                bladeNormals[iNormal + 5] = BLnormal.z;
                const UPnormal = new THREE.Vector3(0, 0, 1);
                // UPnormal.applyAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI/6);
                bladeNormals[iNormal + 6] = UPnormal.x;
                bladeNormals[iNormal + 7] = UPnormal.y;
                bladeNormals[iNormal + 8] = UPnormal.z;
                // indices
                const index = (iX * details + iZ) * 3;
                indices.push(index + 0, index + 1, index + 2);
            }
        }

        this.geometry = new THREE.BufferGeometry();
        this.geometry.setAttribute('center', new THREE.BufferAttribute(bladeCenters, 2));
        this.geometry.setAttribute('position', new THREE.BufferAttribute(bladePositions, 3));
        this.geometry.setIndex( indices );
        // console.log(this.geometry);

        this.material = new THREE.MeshBasicNodeMaterial();

        // Uniforms
        const targetPos = new THREE.Vector3(0, 0, 0); // chunk target position
        this.targetPos = targetPos;
        this.uTargetPos = TSL.uniform(this.targetPos, 'vec3').toVar('uTarget');
        const grassChunkSize = TSL.uniform(chunkSize);

        const center = TSL.attribute('center', 'vec2');

        const rotate2d = TSL.Fn( ( [uv, angle, pivot] ) => {
            return TSL.vec2(
                TSL.cos(angle).mul(uv.x.sub(pivot.x)).add(TSL.sin(angle).mul(uv.y.sub(pivot.y))).add(pivot.x),
                TSL.cos(angle).mul(uv.y.sub(pivot.y)).sub(TSL.sin(angle).mul(uv.x.sub(pivot.x))).add(pivot.y)
            );
        });

        const spawnBlades = TSL.Fn( () => {
            const calculateCenter = TSL.Fn( () => {
                const newCenter = center.toVar('newCenter');
                newCenter.xy = newCenter.xy.sub(this.uTargetPos.xz);
                // Move blades in opposite direction to chunk and wrap
                const uChunkSize = grassChunkSize.toVar('uChunkSize');
                const halfChunkSize = uChunkSize.mul(0.5).toVar('halfChunkSize');
                newCenter.x = TSL.mod(newCenter.x.add(halfChunkSize), uChunkSize).sub(halfChunkSize);
                newCenter.y = TSL.mod(newCenter.y.add(halfChunkSize), uChunkSize).sub(halfChunkSize);
                return newCenter;
            });
            
            const newCenter = calculateCenter();
            
            // Compute center position in world space
            const modelCenter = TSL.modelWorldMatrix.mul(TSL.vec4(newCenter.x, 0.0, newCenter.y, 1.0))
                .toVar('modelCenter');
            
            // Move blade to center position
            const modelPosition = TSL.modelWorldMatrix.mul(TSL.vec4(TSL.positionGeometry, 1.0))
                .toVar('modelPosition');
            modelPosition.xz = modelPosition.xz.add(newCenter);

            // Blend color
            const vWorldPosition = TSL.varying(modelCenter.xyz, 'vWorldPosition');
            // const blend = TSL.step(0.0, vWorldPosition.x).toVar('blend');
            const blend = TSL.texture(
                noiseTexture,
                TSL.vec2(vWorldPosition.x, vWorldPosition.z).div(4.0)
            ).r;

            // Rotate blade to face camera
            const rotation = TSL.atan(
                TSL.cameraPosition.x.sub(modelCenter.x),
                TSL.cameraPosition.z.sub(modelCenter.z),
            ).toVar('angleToCamera');
            modelPosition.xz = rotate2d(modelPosition.xz, rotation, modelCenter.xz);

            // Tilt blades with wind
            const noiseUV = modelPosition.xz.mul(0.05).add(TSL.time.mul(0.05))
                .toVar('noiseUV');
            const windNoise = TSL.texture(
                noiseTexture,
                noiseUV).r
                .mul(1).sub(0.5)
                .toVar('windNoise');
            modelPosition.x = modelPosition.x.add(modelPosition.y.mul(windNoise));

            const viewPosition = TSL.cameraViewMatrix.mul(modelPosition);
            const projectedPosition = TSL.cameraProjectionMatrix.mul(viewPosition);

            // Compute tipness
            const tipness = TSL.varying(
                TSL.oneMinus(TSL.step(0.5,TSL.vertexIndex.toFloat().mod(3))).toVar('tipness'),
                'vTipness');
            const grassColor1 = new THREE.Vector3(0, 1.0, 0.1);
            const grassColor2 = new THREE.Vector3(0.9, 0.3, 0.0);
            const blendColor = TSL.mix(grassColor1, grassColor2, blend);
            // this.bladeColor = bladeColor;
            // const grassColor = TSL.uniform(this.bladeColor).toVar('grassColor');
            // this.material.colorNode = TSL.vec4(grassColor.mul(tipness), 1);
            this.material.colorNode = TSL.vec4(TSL.vec3(blendColor).mul(tipness), 1);

            return projectedPosition;
        })

        this.material.vertexNode = spawnBlades();

    }

    setTargetPosition(position) {
        this.position.set(position.x, 0, position.z);
        this.targetPos.set(position.x, 0, position.z);
    }

    setBladeColor(value) {

    }

    dispose() {
        this.geometry.dispose();
        this.material.dispose();
    }
}